// PowDiffExponentsTask.js
// Iloraz potęg – a^m : a^n = a^{m-n}
// Wersja z losową formą dzielenia (dwukropek lub kreska ułamkowa)

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// przy exp=1 pokazujemy samą podstawę
const factorLatex = (a, exp) => (exp === 1 ? `${a}` : `${a}^{${exp}}`);

// Postać zapisu jest losowana raz podczas generowania zadania.
// Dzięki temu nie zmienia się przy ponownym renderze po kliknięciu.
const divisionLatex = (numeratorLatex, denominatorLatex, divisionForm) => {
  if (divisionForm === "fraction") {
    // \dfrac – większy, czytelniejszy ułamek w trybie inline
    return `\\dfrac{${numeratorLatex}}{${denominatorLatex}}`;
  }

  // klasyczny zapis z dwukropkiem
  return `${numeratorLatex}\\,:\\,${denominatorLatex}`;
};

export function generateTask() {
  const basePool = [2, 3, 4, 5, 6, 7, 8, 9];
  const a = pick(basePool);

  // n ∈ {1..8}, a następnie m losowane z przedziału {n+1..9}
  // (zapewnia m > n, więc m-n ≥ 1 – unikamy wykładnika 0)
  const n = pick([1, 2, 3, 4, 5, 6, 7, 8]);
  const mCandidates = Array.from({ length: 9 - n }, (_, i) => n + 1 + i); // [n+1, ..., 9]
  const m = pick(mCandidates);
  const divisionForm = Math.random() < 0.5 ? "fraction" : "colon";
  const leftLatex = divisionLatex(
    factorLatex(a, m),
    factorLatex(a, n),
    divisionForm
  );

  return {
    type: "PowDiffExponentsTask",
    data: { a, m, n, divisionForm, leftLatex },
  };
}

export function renderTask({ data }) {
  const { a, leftLatex } = data;

  return (
    <div style={{ marginBottom: "12px" }}>
      <div>
        Przedstaw w postaci jednej potęgi o podstawie{" "}
        <InlineMath math={`${a}`} />:
      </div>
      <BlockMath math={leftLatex} />
    </div>
  );
}


export function renderInput(task, userAnswer, onChange, disabled) {
  const handleChange = (ans) => onChange?.(ans);
  return (
    <div>
      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        placeholder="np. 5^2"
        label=""
        powerPreview={true}
      />
      <div aria-hidden style={{ marginTop: "-16px" }} />
      <div aria-hidden style={{ height: "12px" }} />
    </div>
  );
}

// Parsowanie wpisu typu "2^7"
function parsePowerInput(raw) {
  if (!raw) return null;
  const match = raw.replace(/\s/g, "").match(/^(\d+)\^(\d+)$/);
  if (!match) return null;
  return { base: parseInt(match[1], 10), exp: parseInt(match[2], 10) };
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return false;
  const parsed = parsePowerInput(userAnswer.raw);
  if (!parsed) return false;
  const { a, m, n } = data;
  return parsed.base === a && parsed.exp === m - n;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw)
    return <i>(brak odpowiedzi)</i>;

  const raw = String(userAnswer.raw).trim().replace(/\s+/g, "");
  let latex = raw;
  const match = raw.match(/^(\d+)\^(\d+)$/);
  if (match) latex = `${match[1]}^{${match[2]}}`;

  return <InlineMath math={latex} />;
}

export function renderAnswer({ data }) {
  const { a, m, n } = data;
  return <InlineMath math={`${a}^{${m - n}}`} />;
}

export function renderExplanation({ data }) {
  const { a, m, n } = data;
  // W wyjaśnieniu zostawiamy klasyczny zapis z dwukropkiem (czytelna reguła)
  const leftLatex = `${factorLatex(a, m)}\\,:\\,${factorLatex(a, n)}`;

  return (
    <div style={{ display: "grid", rowGap: "8px" }}>
      <p><strong>Krok 1:</strong> Iloraz potęg o tej samej podstawie:</p>
      <BlockMath math={'a^{m}:a^{n}=a^{m-n}'} />

      <p><strong>Krok 2:</strong> Podstaw i sprowadź do jednej potęgi:</p>
      <BlockMath math={`${leftLatex}\\;=\\;${a}^{${m}-${n}}\\;=\\;${a}^{${m - n}}`} />
    </div>
  );
}
